import { useEffect, useState, memo, useCallback } from 'react';
import Link from 'next/link';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { ScrollArea } from '@sourcepilot/ui/framework/ui/atoms/scroll-area';
import { usePartDeltaStreaming } from '@sourcepilot/ui/framework/data/dag-sync/hooks';
import { appendChatParams, useChatUrlParams } from '@sourcepilot/ui/framework/data/url-helpers';

import { TextPartReadAPISchema } from '@sourcepilot/generated/chat/types/textpart';
import { z } from 'zod';

interface TextPartRendererProps {
  part: z.infer<typeof TextPartReadAPISchema>; // Resolved TextPart data object
  isStreaming: boolean;
}

function TextPartRendererComponent({ part, isStreaming }: TextPartRendererProps) {
  const [content, setContent] = useState(part.content || '');
  const chatParams = useChatUrlParams();
  
  // Memoize the callback to prevent infinite re-subscriptions
  const handleDelta = useCallback((delta: any) => {
    // Append only the latest delta's content
    if (delta.delta.delta_data.content_delta) {
      setContent((prev: string) => prev + delta.delta.delta_data.content_delta);
    }
  }, []);
  
  // Subscribe to streaming deltas if this part is streaming
  usePartDeltaStreaming(
    part.is_streaming ? (part.id || null) : null,
    0, // startSequence
    handleDelta
  );

  // Reset content when part changes
  useEffect(() => {
    setContent(part.content || '');
  }, [part.content]);

  return (
    <div className="flex flex-col flex-grow min-w-0">
      <div className="p-[5px] min-w-0">
        <div className="markdown-content text-justify min-w-0 break-words">
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeKatex]}
            components={{
              p: ({ children }) => <p className="leading-relaxed inline">{children}</p>,
              // Keep wide tables inside the panel: scroll horizontally with
              // the app's custom scrollbar instead of forcing the panel wider.
              table: ({ children }) => (
                <ScrollArea orientation="horizontal" className="my-2 w-full max-w-full">
                  <table>{children}</table>
                </ScrollArea>
              ),
              // Long code blocks scroll horizontally too (custom scrollbar).
              pre: ({ children }) => (
                <ScrollArea orientation="horizontal" className="my-2 w-full max-w-full">
                  <pre className="!my-0">{children}</pre>
                </ScrollArea>
              ),
              h3: ({ children }) => (
                <h3 style={{
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  lineHeight: '1.5rem',
                  color: '#1F2937',
                  textDecoration: 'underline',
                  margin: '1.0rem 0 0.1rem 0'
                }}>
                  {children}
                </h3>
              ),
              h4: ({ children }) => (
                <h4 style={{ 
                  fontWeight: 'bold', 
                  color: '#1F2937', 
                  margin: '0.25rem 0 0.25rem 0' 
                }}>
                  {children}
                </h4>
              ),
              h5: ({ children }) => (
                <h5 style={{ 
                  fontWeight: 'medium', 
                  color: '#1F2937', 
                  margin: '0.25rem 0 0.25rem 0' 
                }}>
                  {children}
                </h5>
              ),
              ul: ({ children }) => (
                <ul style={{ margin: '0 0 1.0rem 1.25rem' }}>
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol style={{ margin: '0 0 1.0rem 1.25rem' }}>
                  {children}
                </ol>
              ),
              li: ({ children }) => (
                <li style={{ margin: '0.2rem 0 0 0' }}>
                  {children}
                </li>
              ),
              a: ({ children, href }) => {
                // External links (http://, https://, mailto:) open in a new tab;
                // intra-app links use Next <Link> and preserve the `?chat=`
                // param so the chat side panel stays open after navigation.
                const isExternal =
                  !!href && /^(?:[a-z]+:)?\/\//i.test(href);
                if (isExternal || !href) {
                  return (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#4d30a0', cursor: 'pointer' }}
                    >
                      {children}
                    </a>
                  );
                }
                return (
                  <Link
                    href={appendChatParams(href, chatParams)}
                    style={{ color: '#4d30a0', cursor: 'pointer' }}
                  >
                    {children}
                  </Link>
                );
              },
            }}
          >
            {content}
          </ReactMarkdown>
          {/* Streaming indicator - always inline with the text */}
          {(isStreaming) && (
            <span className="inline-block w-2 h-4 ml-0.5 bg-gray-400 animate-pulse" />
          )}
        </div>
      </div>
    </div>
  );
}

// Custom comparison function for memoization
function areEqual(prevProps: TextPartRendererProps, nextProps: TextPartRendererProps) {
  return (
    prevProps.part.id === nextProps.part.id &&
    prevProps.isStreaming === nextProps.isStreaming &&
    prevProps.part.content === nextProps.part.content
  );
}

export const TextPartRenderer = memo(TextPartRendererComponent, areEqual);